import './main.css'

import { captureError, installEarlyErrorMonitoring } from './services/errorMonitoring'

// NOTE(Rok Kos): this is to improve first time load and lazy loading.
installEarlyErrorMonitoring()

// A chunk that fails to load is usually from a deploy that replaced it, so one
// reload fetches the new build. If it fails again right after a reload, let the
// error propagate to the error boundary (and Sentry) instead of looping.
const PRELOAD_RELOAD_KEY = 'sloyd_preload_reload'
const PRELOAD_RELOAD_WINDOW = 60
let reloading = false

window.addEventListener('vite:preloadError', (event) => {
  // Several chunks can fail together; once reloading, swallow the rest.
  if (reloading) return event.preventDefault()
  const now = Date.now() / 1000
  try {
    const lastReload = Number(sessionStorage.getItem(PRELOAD_RELOAD_KEY))
    if (now - lastReload < PRELOAD_RELOAD_WINDOW) return
    sessionStorage.setItem(PRELOAD_RELOAD_KEY, String(now))
  } catch {
    // Without storage a reload could loop; let the error propagate instead.
    return
  }
  reloading = true
  event.preventDefault()
  window.location.reload()
})

const container = document.getElementById('root') ?? document.body

const isImageHandoff = window.location.pathname === '/image-handoff'
const imageHandoffReady = !isImageHandoff || (await (await import('./services/landingImageHandoff')).receiveLandingImageHandoff(container))

const isConverterRoute = window.location.pathname.startsWith('/labs/3dfileconverter')
const callbackParams = new URLSearchParams(window.location.search)
const isPaymentCallback =
  /^\/payment\/subscription\.(plus|pro)\/(monthly|yearly)\/?$/.test(window.location.pathname) &&
  callbackParams.has('state') &&
  (callbackParams.has('code') || callbackParams.has('error'))

const IMAGE_REFERRER_PATTERNS = ['/image-to-3d', '/es/image-to-3d', '/tools/file-converter']
const SLOYD_MARKETING_HOSTS = ['www.sloyd.ai', 'sloyd.ai']

if (window.location.pathname === '/') {
  try {
    const ref = new URL(document.referrer)
    const isMarketingSite = SLOYD_MARKETING_HOSTS.includes(ref.hostname)
    const isImagePage = IMAGE_REFERRER_PATTERNS.some((p) => ref.pathname.startsWith(p))
    if (isMarketingSite && isImagePage) {
      const params = new URLSearchParams(window.location.search)
      params.set('mode', 'image')
      window.history.replaceState(null, '', `/create-3d?${params}` + window.location.hash)
      sessionStorage.setItem('sloyd_image_referrer', ref.pathname)
    }
  } catch {
    // invalid or empty referrer — do nothing
  }
}

if (imageHandoffReady && isPaymentCallback) {
  const { startDirectCheckout } = await import('./bootstrap/direct-checkout')
  await startDirectCheckout()
} else if (imageHandoffReady && isConverterRoute) {
  const { renderLightweightConverter } = await import('./bootstrap/lightweight-converter-app')
  renderLightweightConverter(container)
} else if (imageHandoffReady) {
  try {
    // NOTE(Rok Kos): this is to improve first time load and lazy loading.
    // Load only dependencies required for the first render.
    await import('@mantine/carousel/styles.css')
    const [
      { Suspense },
      { createRoot },
      { BrowserRouter: Router },
      { isLocalhost },
      { SloydProvider },
      { ABTestTheme },
      { AppErrorBoundary },
      { AppContainer },
      { store },
    ] = await Promise.all([
      import('react'),
      import('react-dom/client'),
      import('react-router-dom'),
      import('./packages/shared-base/src/localhost'),
      import('./packages/store-base/src/provider'),
      import('./packages/sloyd-ui/src/theme'),
      import('./bootstrap/AppErrorBoundary'),
      import('./bootstrap/App.container'),
      import('./bootstrap/main.config'),
    ])

    const containerWithRoot = container as any
    if (!containerWithRoot._root) containerWithRoot._root = createRoot(container)

    const app = <AppContainer />
    containerWithRoot._root.render(
      <SloydProvider store={store}>
        <Suspense>
          <ABTestTheme>
            <Router>{isLocalhost() ? app : <AppErrorBoundary>{app}</AppErrorBoundary>}</Router>
          </ABTestTheme>
        </Suspense>
      </SloydProvider>,
    )

    if (!isLocalhost()) {
      // NOTE(Rok Kos): this is to improve first time load and lazy loading.
      let monitoringStarted = false
      const startMonitoring = () => {
        if (monitoringStarted) return
        monitoringStarted = true
        void import('./bootstrap/main.bugs')
      }
      requestAnimationFrame(() => window.setTimeout(startMonitoring, 0))
      window.setTimeout(startMonitoring, 1000)
    }
  } catch (err) {
    if (window.location.hostname !== 'localhost') {
      captureError(err, { tags: { error_source: 'bootstrap' } })
      await import('./bootstrap/main.bugs').catch(() => {})
    }
    throw err
  }
}
